import { useEffect, useRef, useState } from "react";

import type { ModelViewerElement } from "@/types/model-viewer";
import { applyMatteGrey } from "./applyMatteGrey";

// GLB preview via Google's <model-viewer>, lazy-loaded from CDN once (mirrors the
// POC's CDN-based three.js approach — no npm dependency, no build weight).
const MODEL_VIEWER_CDN =
  "https://unpkg.com/@google/model-viewer@4.0.0/dist/model-viewer.min.js";

let loaderPromise: Promise<void> | null = null;

function ensureModelViewer(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.customElements?.get("model-viewer")) return Promise.resolve();
  if (loaderPromise) return loaderPromise;
  loaderPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.type = "module";
    script.src = MODEL_VIEWER_CDN;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load the 3D viewer."));
    document.head.appendChild(script);
  });
  return loaderPromise;
}

type Cad3dViewerProps = {
  src: string;
  alt?: string;
  className?: string;
  height?: number;
};

export const Cad3dViewer = ({
  src,
  alt = "CAD part preview",
  className = "",
  height = 360,
}: Cad3dViewerProps) => {
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const viewerRef = useRef<ModelViewerElement | null>(null);

  useEffect(() => {
    let active = true;
    ensureModelViewer()
      .then(() => active && setReady(true))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, []);

  // Re-tone every loaded part to the POC matte-grey (NEW Task#2) and drive a load
  // progress bar (C7 — POC parity stepcheck.html :271-273).
  useEffect(() => {
    if (!ready) return;
    const el = viewerRef.current;
    if (!el) return;
    setProgress(0);
    setLoaded(false);
    const onLoad = (): void => {
      setLoaded(true);
      applyMatteGrey(el);
    };
    const onProgress = (e: Event) => {
      const detail = (e as CustomEvent<{ totalProgress?: number }>).detail;
      if (detail && typeof detail.totalProgress === "number") {
        setProgress(detail.totalProgress);
      }
    };
    el.addEventListener("load", onLoad);
    el.addEventListener("progress", onProgress);
    applyMatteGrey(el); // in case the model is already loaded
    return () => {
      el.removeEventListener("load", onLoad);
      el.removeEventListener("progress", onProgress);
    };
  }, [ready, src]);

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-500 ${className}`}
        style={{ height }}
      >
        3D viewer could not be loaded.
      </div>
    );
  }

  if (!ready) {
    return (
      <div
        className={`flex items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-500 ${className}`}
        style={{ height }}
      >
        Loading 3D viewer…
      </div>
    );
  }

  return (
    // POC parity (stepcheck.html L1151-1159): grey #fafafa backdrop + dim
    // exposure + neutral tone-mapping so the grey #ccc part reads matte/greyish
    // instead of the bright PBR "whiteish" model-viewer default.
    <div className="relative" style={{ width: "100%" }}>
      <model-viewer
        ref={viewerRef}
        src={src}
        alt={alt}
        camera-controls
        auto-rotate
        shadow-intensity="1"
        exposure="0.78"
        tone-mapping="neutral"
        interaction-prompt="none"
        className={`rounded-xl border border-gray-200 ${className}`}
        style={{ width: "100%", height, backgroundColor: "#fafafa" }}
      />
      {!loaded && (
        <div className="absolute inset-x-0 bottom-0 h-1.5 overflow-hidden rounded-b-xl bg-gray-200">
          <div
            className="h-full bg-penn-500 transition-[width] duration-150 ease-out"
            style={{ width: `${Math.max(4, Math.round(progress * 100))}%` }}
          />
        </div>
      )}
    </div>
  );
};
